import { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { AxiosError } from 'axios';

import { getCurrentUser, refreshAuthSession } from '../constants/api';
import { IApiErrorResponse } from '../constants/api-interfaces';
import { useAuth } from '../contexts/AuthContext';

type Props = {
  children: React.ReactNode;
};

function isUnauthorizedSessionError(error: unknown): boolean {
  const axiosError = error as AxiosError<IApiErrorResponse>;
  const statusCode = axiosError.response?.status;

  return statusCode === 401 || statusCode === 403;
}

export default function DashboardAuthGuard({ children }: Props) {
  const { authDispatch } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const resolveSession = async () => {
      setIsCheckingSession(true);

      try {
        const currentUserResponse = await getCurrentUser();

        if (!isMounted) {
          return;
        }

        authDispatch({ type: 'setAuth', payload: currentUserResponse.data });
      } catch (error) {
        if (!isUnauthorizedSessionError(error)) {
          if (!isMounted) {
            return;
          }

          authDispatch({ type: 'setAuth', payload: null });
          navigate('/login', { replace: true });
          return;
        }

        try {
          const refreshedSessionResponse = await refreshAuthSession();

          if (!isMounted) {
            return;
          }

          authDispatch({ type: 'setAuth', payload: refreshedSessionResponse.data });
        } catch {
          if (!isMounted) {
            return;
          }

          authDispatch({ type: 'setAuth', payload: null });
          navigate('/login', { replace: true });
          return;
        }
      } finally {
        if (isMounted) {
          setIsCheckingSession(false);
        }
      }
    };

    void resolveSession();

    return () => {
      isMounted = false;
    };
  }, [authDispatch, location.pathname, navigate]);

  if (isCheckingSession) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
}